/**
 * Keep in sync with public/js/toolpath-export.js
 * KUKA throughput calibration module pair.
 * DirectoryLoader needs Toolpath.src + Toolpath.dat with matching names.
 * A lone toolpath.src (or a browser rename like "toolpath (1).src") is rejected.
 */

export const TOOLPATH_MODULE = 'Toolpath';
export const CALIB_PREFLOW_SEC = 40;
export const CALIB_COLLECT_SEC = 60;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const encoder = new TextEncoder();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function u16(value) {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value) {
  return [
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff
  ];
}

function sanitizeComment(value, fallback) {
  const raw = String(value == null ? '' : value).replace(/[\r\n;]+/g, ' ').trim();
  return raw || fallback;
}

export function generateToolpathSrc() {
  return `&ACCESS RVO
&REL 1
&COMMENT Throughput calibration - arm does not move
DEF Toolpath( )
;==========================================================================
; EXTRUDER THROUGHPUT CALIBRATION
;
; WHAT IT IS FOR
;   The slicer needs to know what screw speed gives a wanted flow rate.
;   That depends on the material, not on the machine, so it has to be
;   measured whenever the material changes. This runs the screw at each
;   set speed and stops after each one so the output can be caught and
;   weighed. The weights make the curve.
;
;   The curve itself is worked out on the knowledge centre page, not here:
;     https://knowledge-centre.rapidfusion.co.uk/calibration
;   Type in the speeds and the weights and it gives the four coefficients
;   for the slicer. The same page generated this file and Toolpath.dat
;   next to it.
;
; BEFORE STARTING
;   1) Extruder up to temperature. This is the only thing checked.
;   2) Program override 100 %, extrusion multiplier 100 %. NEITHER IS
;      CHECKED - that is on you.
;   3) Scale, a bucket or tray, and something to cut the extrudate with.
;
;   T1 or AUT, no mode is enforced.
;
; RUNNING IT
;   Select and press START. Then, for each point:
;     - Pre-flow runs for 40 s and is thrown away.
;     - "CUT NOW - START catching" appears. Cut the extrudate away and
;       put the bucket under the nozzle.
;     - 60 s later "CUT NOW - STOP catching" appears. Cut again and take
;       the bucket away.
;     - The screw stops and the program pauses. Weigh what was caught and
;       write it down against the speed named in the message.
;     - Press START for the next point.
;
;   The screw runs continuously for the whole 100 s. It does not stop
;   between the pre-flow and the sample - only what you do with the
;   output changes.
;
; WHY THE ARM STAYS PUT
;   If the arm moved, flow through the nozzle would change with it and
;   the weight would no longer belong to the speed that was set.
;   PTP $AXIS_ACT holds station without commanding a target.
;
; THE SPEEDS, THE COUNT AND THE TIMINGS LIVE IN Toolpath.dat
;==========================================================================
DECL INT nPoint
DECL INT nRpm
DECL INT nTotal
DECL CHAR sMsg[100]
DECL INT nOff
DECL STATE_T sStat

  ;FOLD INI
  BAS (#INITMOV,1 )
  ;ENDFOLD (INI)

  $BASE = BASE_DATA[1]
  BED_Z_ACTIVE = BASE_DATA[1].Z
  $TOOL = TOOL_DATA[1]
  $LOAD = LOAD_DATA[1]
  BAS(#BASE,1)
  $IPO_MODE = #BASE
  BAS(#TOOL,1)

  ; Stay where you are - no LIN / no PTP target moves
  PTP $AXIS_ACT

  IF (NOT (COMMISSIONING_MODE OR extruderReady)) THEN
    MsgQuit("Extruder is not up to temperature. Wait for it, then start again.","Calib")
    HALT
    RETURN
  ENDIF

  nTotal = CALIB_PREFLOW_SEC + CALIB_COLLECT_SEC

  sMsg[] = " "
  nOff = 0
  SWRITE(sMsg[], sStat, nOff, "Override %d pct - MUST be 100. %d s flow per point.", $OV_PRO, nTotal)
  MsgNotify(sMsg[], "Calib")

  FOR nPoint = 1 TO CALIB_COUNT

    nRpm = CALIB_RPM[nPoint]

    IF (CALIB_RPM[nPoint] <= 0.0) THEN
      sMsg[] = " "
      nOff = 0
      SWRITE(sMsg[], sStat, nOff, "Point %d is set to zero. Skipping it.", nPoint)
      MsgNotify(sMsg[], "Calib")
    ELSE

      sMsg[] = " "
      nOff = 0
      SWRITE(sMsg[], sStat, nOff, "Point %d of %d, %d rpm. Pre-flow - do NOT catch yet.", nPoint, CALIB_COUNT, nRpm)
      MsgNotify(sMsg[], "Calib")

      START_SERVO = TRUE
      SPEED = 0
      WAIT SEC 1

      SPEED = CALIB_RPM[nPoint]
      WAIT SEC CALIB_PREFLOW_SEC

      sMsg[] = " "
      nOff = 0
      SWRITE(sMsg[], sStat, nOff, "CUT NOW - START catching. %d rpm, %d s.", nRpm, CALIB_COLLECT_SEC)
      MsgNotify(sMsg[], "Calib")

      WAIT SEC CALIB_COLLECT_SEC

      sMsg[] = " "
      nOff = 0
      SWRITE(sMsg[], sStat, nOff, "CUT NOW - STOP catching. Point %d, %d rpm.", nPoint, nRpm)
      MsgNotify(sMsg[], "Calib")

      SPEED = 0
      WAIT SEC 1
      START_SERVO = FALSE

      sMsg[] = " "
      nOff = 0
      SWRITE(sMsg[], sStat, nOff, "Point %d done at %d rpm. Weigh it, note it, press START.", nPoint, nRpm)
      MsgNotify(sMsg[], "Calib")
      HALT

    ENDIF

  ENDFOR

  MsgNotify("All points done. Put the speeds and weights into the knowledge centre page.","Calib")

END ;(Toolpath)
`;
}

export function generateToolpathDat(rpms, meta = {}) {
  const slots = rpms.map((v) => Number(v));
  const count = slots.length;
  const table = slots
    .map((v, i) => `CALIB_RPM[${i + 1}]=${v.toFixed(1)}`)
    .join('\n');
  const material = sanitizeComment(meta.materialName, 'not set');
  const nozzle = meta.nozzleSize ? `${meta.nozzleSize} mm` : 'not set';
  const range = (meta.minRpm && meta.maxRpm)
    ? `${meta.minRpm} - ${meta.maxRpm} rpm`
    : 'as entered';
  const generated = sanitizeComment(meta.date, new Date().toLocaleDateString());

  return `&ACCESS RVO
&REL 1
&COMMENT Throughput calibration - ${count} speeds, arm does not move
DEFDAT Toolpath
;==========================================================================
; THE CALIBRATION POINTS
;
; Generated by the Knowledge Centre throughput tool.
;   Material    ${material}
;   Nozzle      ${nozzle}
;   Speed range ${range}
;   Generated   ${generated}
;
; Change these on the pendant and nothing else needs touching. Navigator,
; open this file, edit the number, close.
;
; Fastest first, deliberately. Starting at the top speed gets the barrel
; flowing properly straight away.
;
; Setting a speed to 0.0 makes the program skip that point.
;==========================================================================
DECL REAL CALIB_RPM[${count}]
${table}

; How many rows above. The program loops 1 to this.
DECL INT CALIB_COUNT=${count}

;==========================================================================
; THE TWO TIMINGS
;
; CALIB_PREFLOW_SEC  screw runs, output is thrown away.
; CALIB_COLLECT_SEC  the measured window. 60 s makes grams per minute.
;
; The screw does not stop between the two.
;==========================================================================
DECL INT CALIB_PREFLOW_SEC=${CALIB_PREFLOW_SEC}
DECL INT CALIB_COLLECT_SEC=${CALIB_COLLECT_SEC}

ENDDAT
`;
}

export function buildStoreZip(files) {
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const data = typeof file.data === 'string' ? encoder.encode(file.data) : file.data;
    const crc = crc32(data);
    const localHeader = [
      0x50, 0x4b, 0x03, 0x04,
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0)
    ];
    localParts.push(Uint8Array.from(localHeader), nameBytes, data);

    const centralHeader = [
      0x50, 0x4b, 0x01, 0x02,
      ...u16(20),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset)
    ];
    centralParts.push(Uint8Array.from(centralHeader), nameBytes);
    offset += localHeader.length + nameBytes.length + data.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const eocd = Uint8Array.from([
    0x50, 0x4b, 0x05, 0x06,
    ...u16(0),
    ...u16(0),
    ...u16(files.length),
    ...u16(files.length),
    ...u32(centralSize),
    ...u32(offset),
    ...u16(0)
  ]);

  const total = offset + centralSize + eocd.length;
  const out = new Uint8Array(total);
  let cursor = 0;
  [...localParts, ...centralParts, eocd].forEach((part) => {
    out.set(part, cursor);
    cursor += part.length;
  });
  return out;
}

export function buildToolpathZipBytes(rpms, meta = {}) {
  return buildStoreZip([
    { name: `${TOOLPATH_MODULE}.src`, data: generateToolpathSrc() },
    { name: `${TOOLPATH_MODULE}.dat`, data: generateToolpathDat(rpms, meta) }
  ]);
}

export function downloadToolpathZip(rpms, meta = {}) {
  const bytes = buildToolpathZipBytes(rpms, meta);
  const blob = new Blob([bytes], { type: 'application/zip' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${TOOLPATH_MODULE}.zip`;
  link.click();
  URL.revokeObjectURL(link.href);
}
